import styled from "@emotion/styled";
import { Link } from "react-router-dom";
import type { SlotStatus, SlotTrendPoint } from "@clustec/common";
import { theme } from "../lib/theme";
import { orderedStatusCounts, slotStatusColor } from "./SlotStatusLabel";

/**
 * Outcome mix per epoch across the recent window — one stacked column per epoch.
 *
 * Deliberately a column strip rather than a line sparkline, so it cannot be confused with the
 * per-slot sparklines above it: those are continuous lines over slot positions, this is discrete
 * columns over epochs. It's the same visual language as the epoch timeline — a strip of segments —
 * one zoom level out, which is the mental model worth encouraging: the same picture at slot
 * resolution and at epoch resolution.
 *
 * Columns are keyed by epoch number, not by array index. The trend endpoint only returns buckets
 * that have rows, so a window with a coverage gap comes back short — indexing by position would
 * silently shift every later epoch left and mislabel the whole strip.
 *
 * It used to draw ONE bar per epoch whose height was the miss rate and whose colour was a rate
 * band — green under 15%, orange under 50%, red above. Two things made that unreadable. A green
 * bar meant "slots were missed, just not many", inverting what green means everywhere else, and a
 * clean epoch drew no bar at all, so the healthy state was the blank one. And the colour
 * contradicted the cause legend sitting directly beneath it: an epoch whose every miss was
 * `blocks-missed` — red in the legend, red on the timeline, red in `slotStatusColor` — still
 * rendered green because the rate was low.
 *
 * Now each column is the epoch's slots stacked by what actually happened, full height, using
 * `slotStatusColor` so a colour means the same thing here as everywhere else. A clean epoch is a
 * full green column: measured, and fine. Misses are visible as the coloured cap eating into it,
 * in the colour of the cause.
 *
 * One of those causes is not a sentinel status. An epoch whose checkpoints reached L1 and were then
 * discarded by a rollup prune stacks as `orphaned`, in a slate outside the success/warning/danger
 * ramp — the case that mattered most and drew as a full green column before, because the sentinel
 * still reports every one of those slots as mined.
 */

const Wrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 34px;
`;

const Column = styled(Link)`
  flex: 1;
  min-width: 3px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  text-decoration: none;
  border-radius: 2px;
  overflow: hidden;

  /* Visible under a partially-observed epoch, so a short stack reads as "some slots unaccounted
     for" rather than as a squat bar. */
  background: ${theme.colors.bgHover};

  &:hover {
    outline: 1px solid ${theme.colors.primary};
    outline-offset: 1px;
  }
`;

/**
 * One status's share of the epoch. Stacked bottom-up in severity order, so the mined base sits on
 * the floor and failures pile visibly on top rather than being buried mid-column.
 */
const Slice = styled.span<{ pct: number; color: string; partial: boolean }>`
  display: block;
  width: 100%;
  height: ${(p) => p.pct}%;
  background: ${(p) => p.color};
  /* An in-progress epoch is dimmed: its mix is real but covers a fraction of the slots, so it
     shouldn't read with the same authority as a completed one. */
  opacity: ${(p) => (p.partial ? 0.45 : 1)};
`;

/** No bucket at all — we never observed this epoch, which is different from observing zero misses. */
const MissingColumn = styled.div`
  flex: 1;
  min-width: 3px;
  height: 100%;
  border-radius: 2px;
  background: repeating-linear-gradient(
    45deg,
    ${theme.colors.bgHover},
    ${theme.colors.bgHover} 2px,
    transparent 2px,
    transparent 4px
  );
`;

export interface EpochMissStripProps {
  /** Inclusive epoch range to draw, one column each. */
  fromEpoch: number;
  toEpoch: number;
  points: SlotTrendPoint[];
  slotsPerEpoch: number;
  network: string;
}

export function EpochMissStrip({
  fromEpoch,
  toEpoch,
  points,
  slotsPerEpoch,
  network,
}: EpochMissStripProps) {
  const byEpoch = new Map(points.map((p) => [p.from, p]));
  const epochs: number[] = [];
  for (let e = fromEpoch; e <= toEpoch; e++) epochs.push(e);

  return (
    <Wrapper>
      {epochs.map((e) => {
        const p = byEpoch.get(e);
        if (!p) return <MissingColumn key={e} title={`Epoch ${e} — not observed`} />;
        const partial = p.slotsObserved < slotsPerEpoch;
        const slices = orderedStatusCounts(p.statusCounts);
        // `orphaned` is a derived bucket in statusCounts rather than a sentinel status — see the
        // endpoint's schema. Read by key for that reason, not filtered out of `slices`: it belongs
        // in the stack, drawn in its own colour, directly above the mined base it was taken from.
        const orphanedCount = p.statusCounts["orphaned"] ?? 0;
        // Denominator is the epoch's full length, not the slots observed, so a partial epoch draws
        // a short stack over visible track instead of a full one that overstates its coverage.
        const denominator = Math.max(slotsPerEpoch, p.slotsObserved) || 1;
        return (
          <Column
            key={e}
            to={`/${network}/health?epoch=${e}`}
            title={
              `Epoch ${e} — ${p.missed} of ${p.slotsObserved} slots missed ` +
              `(${p.missedPct.toFixed(2)}%)${partial ? ", in progress" : ""}` +
              // Named separately from the miss rate, because for a pruned epoch the rate is the
              // less interesting number: 2026-08-14's epoch 1274 missed nothing and lost
              // everything. Without this the column would be a wall of orphan slate above a
              // tooltip reporting 0.00%.
              (orphanedCount > 0 ? `\n${orphanedCount} slots discarded by reorg` : "") +
              `\n` +
              slices.map(([status, count]) => `${status}: ${count}`).join("\n")
            }
          >
            {/* Rendered top-down (reverse of the bottom-up stack order) because the column is a
                normal block flow, not a flex column-reverse — first child paints highest. */}
            {[...slices].reverse().map(([status, count]) => (
              <Slice
                key={status}
                pct={(count / denominator) * 100}
                color={slotStatusColor(status)}
                partial={partial}
              />
            ))}
          </Column>
        );
      })}
    </Wrapper>
  );
}
